import { usePwaInstall } from '@logistech/ui';
import { Outlet, Link, useLocation } from '@tanstack/react-router';
import {
  LayoutDashboard, Truck, Map, ListChecks, Wrench, Fuel,
  AlertTriangle, FileBarChart, Settings, MapPinned, Building2, Users, Receipt, Download, CreditCard, UsersRound,
} from 'lucide-react';

import { BottomNav, type NavItem } from './BottomNav';
import { TopBar } from './TopBar';

import { sessionHasPermission, type Permission } from '@/lib/permissions';
import { useLiveUpdates } from '@/lib/useLiveUpdates';
import { cn } from '@/lib/utils';
import { useAuth } from '@/store/auth';



// perm : même table que les beforeLoad des routes (lib/route-guards) ; sans perm, visible de tous
const nav: (NavItem & { perm?: Permission })[] = [
  { to: '/dashboard', label: 'Tableau de bord', shortLabel: 'Accueil', icon: LayoutDashboard },
  { to: '/map', label: 'Carte live', shortLabel: 'Carte', icon: Map, perm: 'vehicles.read' },
  { to: '/fleet', label: 'Flotte', icon: Truck, perm: 'vehicles.read' },
  { to: '/drivers', label: 'Conducteurs', icon: Users, perm: 'vehicles.read' },
  { to: '/missions', label: 'Missions', icon: ListChecks, perm: 'missions.read' },
  { to: '/clients', label: 'Clients', icon: Building2, perm: 'missions.read' },
  { to: '/invoices', label: 'Facturation', icon: Receipt, perm: 'invoices.read' },
  { to: '/payments', label: 'Paiements', icon: CreditCard, perm: 'invoices.read' },
  { to: '/team', label: 'Équipe', icon: UsersRound, perm: 'users.read' },
  { to: '/maintenance', label: 'Maintenance', icon: Wrench, perm: 'maintenance.read' },
  { to: '/fuel', label: 'Carburant', icon: Fuel, perm: 'fuel.read' },
  { to: '/alerts', label: 'Alertes', icon: AlertTriangle, perm: 'alerts.read' },
  { to: '/geofences', label: 'Géofences', icon: MapPinned, perm: 'geofences.read' },
  { to: '/reports', label: 'Rapports', icon: FileBarChart, perm: 'reports.read' },
  { to: '/settings', label: 'Paramètres', icon: Settings },
];

export const AppShell = () => {
  const location = useLocation();
  useLiveUpdates();
  const { canPrompt, iosManual, promptInstall } = usePwaInstall();
  const user = useAuth((s) => s.session?.user);
  const items = nav.filter((item) => !item.perm || sessionHasPermission(user, item.perm));

  return (
    <div className="flex h-screen supports-[height:100dvh]:h-dvh bg-background">
      {/* Sidebar dès md ; en dessous, BottomNav */}
      <aside className="hidden w-60 bg-primary-900 text-white md:flex flex-col shrink-0">
        {/* Logo complet, utilisateur et déconnexion dans la TopBar : ici l'icône seule, alignée sur sa hauteur */}
        <div className="h-14 flex items-center px-4 border-b border-primary-700 shrink-0">
          <img src="/favicon.svg" alt="LogisTech-LGT" className="h-8 w-8" />
        </div>
        <nav aria-label="Navigation principale" className="flex-1 overflow-y-auto py-2">
          {items.map((item) => {
            const active = location.pathname.startsWith(item.to);
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={cn(
                  'flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors',
                  active
                    ? 'bg-primary-700 text-white'
                    : 'text-white/80 hover:bg-primary-700/60 hover:text-white',
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                {item.label}
              </Link>
            );
          })}
        </nav>
        {canPrompt && (
          <button
            onClick={() => void promptInstall()}
            className="flex items-center gap-3 px-4 py-3 text-sm text-white/70 hover:text-white hover:bg-primary-700 border-t border-primary-700 transition-colors"
          >
            <Download className="h-4 w-4" /> Installer l&apos;application
          </button>
        )}
        {iosManual && (
          <p className="px-4 py-3 text-xs text-white/60 border-t border-primary-700">
            Pour installer : bouton Partager, puis « Sur l&apos;écran d&apos;accueil ».
          </p>
        )}
      </aside>
      {/* Sous md : le bas du contenu reste au-dessus de la BottomNav (4 rem + safe area) */}
      <main className="flex-1 flex flex-col min-w-0 overflow-y-auto pb-[calc(4rem+env(safe-area-inset-bottom))] md:pb-0">
        <TopBar />
        <div className="flex-1">
          <Outlet />
        </div>
      </main>
      <BottomNav items={items} />
    </div>
  );
};
